import React, { useState, useEffect, useCallback } from 'react';
import { MdRefresh, MdCheckCircle, MdCancel, MdStorage, MdDns, MdSyncAlt, MdRestartAlt } from 'react-icons/md';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatusDot({ status }) {
  const colors = {
    connected: 'bg-emerald-500',
    disconnected: 'bg-red-500',
    disabled: 'bg-gray-400',
    error: 'bg-amber-500',
  };
  return (
    <span className={`inline-block w-3 h-3 rounded-full ${colors[status] || 'bg-gray-400'} mr-2`} />
  );
}

function SyncIcon({ exists }) {
  if (exists === true) return <MdCheckCircle className="w-5 h-5 text-emerald-500" />;
  if (exists === false) return <MdCancel className="w-5 h-5 text-red-400" />;
  return <span className="text-gray-300">—</span>;
}

export default function AdminRedisHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/redis-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error('Failed to fetch Redis health:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const handleSyncAll = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/redis-health/sync-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      setSyncResult(result);
      fetchHealth();
    } catch (err) {
      setSyncResult({ error: err.message });
    } finally {
      setSyncing(false);
    }
  };

  const handleResetMetrics = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/admin/redis-health/reset-metrics`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchHealth();
    } catch (err) {
      console.error('Failed to reset metrics:', err);
    }
  };

  if (loading && !data) {
    return (
      <div className="p-6 flex justify-center" data-testid="redis-health-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  if (!data) {
    return <div className="p-6 text-gray-500" data-testid="redis-health-error">Failed to load Redis health data.</div>;
  }

  const { connection, key_inventory, cache_metrics, data_layers } = data;
  const metrics = cache_metrics || {};
  const totalHits = metrics.total?.hits || 0;
  const totalMisses = metrics.total?.misses || 0;
  const totalReqs = totalHits + totalMisses;
  const hitRate = totalReqs > 0 ? ((totalHits / totalReqs) * 100).toFixed(1) : '—';

  return (
    <div className="p-6 space-y-6" data-testid="redis-health-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-rubik" data-testid="redis-health-title">Redis Health</h1>
          <p className="text-gray-500 text-sm mt-1 font-rubik">Upstash connection, key inventory, and Supabase sync status</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchHealth}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-card text-sm font-rubik transition-all"
            data-testid="redis-health-refresh"
          >
            <MdRefresh className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleSyncAll}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-card text-sm font-rubik transition-all"
            data-testid="redis-health-sync-all"
          >
            <MdSyncAlt className={`w-4 h-4 ${syncing ? 'animate-pulse' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync All to Supabase'}
          </button>
        </div>
      </div>

      {/* Sync result banner */}
      {syncResult && (
        <div className="card-container border border-primary/30 bg-green-50" data-testid="redis-health-sync-result">
          <p className="text-sm font-rubik text-gray-900 font-medium mb-2">Sync Complete</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-gray-600 font-rubik">
            {Object.entries(syncResult.results || {}).map(([key, val]) => (
              <div key={key} className="bg-white rounded-lg p-3 border border-gray-100">
                <span className="font-medium text-gray-900 capitalize">{key.replace('_', ' ')}</span>
                <pre className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{JSON.stringify(val, null, 1)}</pre>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {/* Connection */}
        <div className="card-container" data-testid="redis-connection-card">
          <div className="flex items-center gap-2 mb-2">
            <MdDns className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-500 font-rubik">Connection</span>
          </div>
          <div className="flex items-center">
            <StatusDot status={connection.status} />
            <span className="text-lg font-bold text-gray-900 font-rubik capitalize" data-testid="redis-connection-status">
              {connection.status}
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-1 font-rubik">{connection.provider}</p>
        </div>

        {/* Total keys */}
        <div className="card-container" data-testid="redis-total-keys-card">
          <div className="flex items-center gap-2 mb-2">
            <MdStorage className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-500 font-rubik">Total Keys</span>
          </div>
          <span className="text-3xl font-bold text-gray-900 font-rubik" data-testid="redis-total-keys">
            {key_inventory.total}
          </span>
          <p className="text-xs text-gray-400 mt-1 font-rubik">{Object.keys(key_inventory.groups || {}).length} groups</p>
        </div>

        {/* Cache hit rate */}
        <div className="card-container" data-testid="redis-hit-rate-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500 font-rubik">Cache Hit Rate</span>
            <button onClick={handleResetMetrics} className="text-gray-400 hover:text-gray-600" title="Reset metrics">
              <MdRestartAlt className="w-4 h-4" />
            </button>
          </div>
          <span className="text-3xl font-bold text-gray-900 font-rubik">{hitRate}%</span>
          <p className="text-xs text-gray-400 mt-1 font-rubik">{totalHits} hits / {totalMisses} misses</p>
        </div>

        {/* Supabase Sync */}
        <div className="card-container" data-testid="redis-supabase-sync-card">
          <div className="flex items-center gap-2 mb-2">
            <MdSyncAlt className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-500 font-rubik">Supabase Tables</span>
          </div>
          {(() => {
            const tables = data.supabase_tables || {};
            const total = Object.keys(tables).length;
            const active = Object.values(tables).filter(Boolean).length;
            return (
              <>
                <span className="text-3xl font-bold text-gray-900 font-rubik">{active}/{total}</span>
                <p className="text-xs text-gray-400 mt-1 font-rubik">persistence tables active</p>
              </>
            );
          })()}
        </div>
      </div>

      {/* Data Layers table */}
      <div className="card-container" data-testid="redis-data-layers">
        <h2 className="text-lg font-bold text-gray-900 font-rubik mb-4">Data Layers</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="pb-3 font-rubik font-medium">Layer</th>
                <th className="pb-3 font-rubik font-medium text-center">Redis Keys</th>
                <th className="pb-3 font-rubik font-medium">Supabase Table</th>
                <th className="pb-3 font-rubik font-medium text-center">Synced</th>
              </tr>
            </thead>
            <tbody>
              {data_layers.map((layer) => (
                <tr key={layer.name} className="border-b border-gray-100 last:border-0" data-testid={`data-layer-${layer.name.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                  <td className="py-3 text-gray-900 font-rubik font-medium">{layer.name}</td>
                  <td className="py-3 text-center">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-rubik font-bold ${
                      layer.redis_keys > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {layer.redis_keys}
                    </span>
                  </td>
                  <td className="py-3 text-gray-500 font-rubik text-xs font-mono">
                    {layer.supabase_table || '—'}
                  </td>
                  <td className="py-3 text-center">
                    <SyncIcon exists={layer.supabase_exists} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Key Groups breakdown */}
      <div className="card-container" data-testid="redis-key-groups">
        <h2 className="text-lg font-bold text-gray-900 font-rubik mb-4">Key Groups</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(key_inventory.groups || {}).sort((a, b) => b[1].count - a[1].count).map(([prefix, info]) => (
            <div key={prefix} className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-testid={`key-group-${prefix}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-rubik text-gray-900 font-medium">{prefix}</span>
                <span className="text-xs font-rubik px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">{info.count}</span>
              </div>
              <div className="space-y-0.5">
                {info.sample_keys.map((k) => (
                  <p key={k} className="text-xs text-gray-400 font-mono truncate">{k}</p>
                ))}
                {info.count > 3 && (
                  <p className="text-xs text-gray-400 font-rubik">+{info.count - 3} more</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cache metrics breakdown */}
      {metrics && metrics.total && (
        <div className="card-container" data-testid="redis-cache-metrics">
          <h2 className="text-lg font-bold text-gray-900 font-rubik mb-4">Cache Metrics by Type</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {Object.entries(metrics)
              .filter(([k]) => k !== 'total' && k !== 'enabled' && k !== 'error')
              .map(([type, m]) => {
                const total = (m.hits || 0) + (m.misses || 0);
                return (
                  <div key={type} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <span className="text-xs text-gray-500 font-rubik capitalize">{type.replace('_', ' ')}</span>
                    <div className="text-lg font-bold text-gray-900 font-rubik mt-1">{m.hit_rate || 0}%</div>
                    <div className="text-xs text-gray-400 font-rubik">{total} requests</div>
                    {total > 0 && (
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                        <div
                          className="bg-emerald-500 h-1.5 rounded-full transition-all"
                          style={{ width: `${m.hit_rate || 0}%` }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
